import { useTranslation } from "@/hooks";
import { fileParserService } from "@/services/data";
import { indexedDBService } from "@/services/storage";
import { formatBytes } from "@/utils/format";
import { logger } from "@/utils/debug";

import {
  useDeleteFile,
  useRefreshFiles,
  useUploadFile,
  useUserFilesStore,
} from "@/stores/user-file";
import {
  ActionIcon,
  Button,
  Center,
  FileButton,
  Flex,
  Group,
  Loader,
  Menu,
  Modal,
  Progress,
  Stack,
  Table,
  Text,
  Title,
} from "@mantine/core";
import {
  IconDatabase,
  IconDotsVertical,
  IconFile,
  IconFileCode,
  IconFileSpreadsheet,
  IconFileText,
  IconTrash,
  IconUpload,
  type TablerIcon,
} from "@tabler/icons-react";
import { useEffect, useState } from "react";
import "./MyDataPage.css";

export function MyDataPage() {
  const { t } = useTranslation();
  const files = useUserFilesStore((state) => state.files);
  const loading = useUserFilesStore((state) => state.loading);
  const uploadFile = useUploadFile();
  const deleteFile = useDeleteFile();
  const refreshFiles = useRefreshFiles();
  const [uploading, setUploading] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [fileToDelete, setFileToDelete] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [storageQuota, setStorageQuota] = useState<{
    quota: number;
    usage: number;
  }>({ quota: 0, usage: 0 });

  useEffect(() => {
    refreshFiles();
    updateStorageQuota();
  }, [refreshFiles]);

  const updateStorageQuota = async () => {
    try {
      const quota = await indexedDBService.getIndexedDBQuota();
      setStorageQuota(quota);
    } catch (error) {
      logger.error("Failed to get storage quota:", error);
    }
  };

  const handleFileUpload = async (file: File | null) => {
    if (!file) return;

    const allowedTypes = [
      "text/csv",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/vnd.ms-excel",
      "application/json",
      "application/geo+json",
      "application/x-parquet",
      "application/parquet",
    ];

    const allowedExtensions = [
      ".csv",
      ".xlsx",
      ".xls",
      ".json",
      ".geojson",
      ".parquet",
    ];
    const fileExtension = "." + file.name.split(".").pop()?.toLowerCase();

    if (
      !allowedTypes.includes(file.type) &&
      !allowedExtensions.includes(fileExtension)
    ) {
      alert(t("myData").uploadOnlyAllowedFiles);
      return;
    }

    try {
      setUploading(true);

      const uploadedFile = await uploadFile(file);

      if (uploadedFile) {
        try {
          const parsedData = await fileParserService.parseFile(file);

          // Create metadata with dataset-compatible fields
          const dbTableId = `user_file_${uploadedFile.id}`;
          await indexedDBService.storeFileData(
            uploadedFile.id,
            parsedData.rows,
            {
              fileId: uploadedFile.id,
              fileName: uploadedFile.name,
              fileType: uploadedFile.type,
              fileSize: file.size,
              rowCount: parsedData.metadata.rowCount,
              columnCount: parsedData.metadata.columnCount,
              columns: parsedData.columns,
              createdAt: uploadedFile.created_at,
              // Dataset-compatible fields
              db_table_id: dbTableId,
              table_name: uploadedFile.name,
              table_name_english: uploadedFile.name,
              description_original: `User uploaded file: ${uploadedFile.name}`,
              description_english: `User uploaded file: ${uploadedFile.name}`,
            }
          );

          await refreshFiles();
          await updateStorageQuota();
        } catch (parseError) {
          logger.error("Failed to parse file:", parseError);
          alert(t("myData").failedToParseFile);
        }
      }
    } catch (error) {
      logger.error("Upload failed:", error);
      alert(t("myData").uploadFailed);
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteClick = (file: { id: string; name: string }) => {
    setFileToDelete(file);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!fileToDelete) return;

    try {
      setDeleting(true);
      await deleteFile(fileToDelete.id);
      setDeleteModalOpen(false);
      setFileToDelete(null);
      await updateStorageQuota();
    } catch (error) {
      logger.error("Delete failed:", error);
      alert(t("myData").deleteFailed);
    } finally {
      setDeleting(false);
    }
  };

  const getFileIcon = (mimeType: string, fileName: string): TablerIcon => {
    if (mimeType.includes("csv") || fileName.endsWith(".csv"))
      return IconFileText;
    if (
      mimeType.includes("excel") ||
      mimeType.includes("spreadsheet") ||
      fileName.endsWith(".xlsx") ||
      fileName.endsWith(".xls")
    )
      return IconFileSpreadsheet;
    if (mimeType.includes("json") || fileName.endsWith(".json"))
      return IconFileCode;
    if (fileName.endsWith(".parquet")) return IconFile;
    return IconFile;
  };

  const getFileType = (mimeType: string) => {
    if (mimeType.includes("csv")) return t("myData").csv;
    if (mimeType.includes("excel") || mimeType.includes("spreadsheet"))
      return t("myData").excel;
    if (mimeType.includes("geo+json")) return t("myData").geojson;
    if (mimeType.includes("json")) return t("myData").json;
    if (mimeType.includes("parquet")) return t("myData").parquet;
    return t("myData").fileType;
  };

  const usage = storageQuota.usage;
  const quota = storageQuota.quota;

  return (
    <Flex h="100%" direction="column" gap="lg" p="lg">
      {/* Header */}
      <Flex direction="column">
        <Group gap="xs" mb="xs">
          <IconDatabase
            color="var(--mantine-color-eu_flag-filled)"
            stroke={1.6}
            size={28}
          />
          <Title fz={24} fw={500}>
            {t("myData").title}
          </Title>
        </Group>
        <Group justify="space-between" align="center">
          <Text fz={16} fw={500} c="gray.9">
            {files.length}{" "}
            {files.length !== 1 ? t("myData").files : t("myData").file}{" "}
            {t("myData").filesUploaded}
          </Text>
          <FileButton
            onChange={handleFileUpload}
            accept=".csv,.xlsx,.xls,.json,.geojson,.parquet"
            disabled={uploading}
          >
            {(props) => (
              <Button
                {...props}
                leftSection={<IconUpload size={16} />}
                loading={uploading}
                color="eu_flag"
              >
                {t("myData").uploadFile}
              </Button>
            )}
          </FileButton>
        </Group>
      </Flex>

      {/* File Table */}
      {loading ? (
        <Center h={200}>
          <Stack align="center" gap="md">
            <Loader size="md" />
            <Text c="gray.9">{t("myData").loadingFiles}</Text>
          </Stack>
        </Center>
      ) : files.length === 0 ? (
        <Center h={200}>
          <Stack align="center" gap="md">
            <IconDatabase size={48} color="var(--mantine-color-gray-4)" />
            <Text c="gray.9" fz="lg">
              {t("myData").noFilesUploaded}
            </Text>
            <Text c="gray.9" fz="sm">
              {t("myData").uploadInstructions}
            </Text>
          </Stack>
        </Center>
      ) : (
        <Stack gap="md">
          {quota > 0 && (
            <Stack gap="xs">
              <Group justify="space-between">
                <Text fz="sm" fw={500} c="gray.9">
                  {t("myData").storageUsage}
                </Text>
                <Text fz="sm" c="gray.9">
                  {formatBytes(usage)} / {formatBytes(quota)} (
                  {Math.round((usage / quota) * 100)}%)
                </Text>
              </Group>
              <Progress
                value={(usage / quota) * 100}
                size="sm"
                color={usage / quota > 0.9 ? "red" : "eu_flag"}
              />
              <Text fz="xs" c="gray.9">
                {formatBytes(Math.max(0, quota - usage))}{" "}
                {t("myData").remaining}
              </Text>
            </Stack>
          )}
          <Table highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t("myData").filename}</Table.Th>
                <Table.Th>{t("myData").type}</Table.Th>
                <Table.Th>{t("myData").size}</Table.Th>
                <Table.Th>{t("myData").uploaded}</Table.Th>
                <Table.Th w={60}></Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {files.map((file) => {
                const FileIcon = getFileIcon(file.type, file.name);
                return (
                  <Table.Tr key={file.id}>
                    <Table.Td>
                      <Group gap="xs">
                        <FileIcon size={22} stroke={1} />
                        <Text fz={16} truncate fw={500}>
                          {file.name}
                        </Text>
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <Text fz={16} c="gray.9">
                        {getFileType(file.type)}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text fz={16} c="gray.9">
                        {formatBytes(file.size)}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text fz={16} c="gray.9">
                        {new Date(file.created_at).toLocaleDateString()}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Menu shadow="md" width={200}>
                        <Menu.Target>
                          <ActionIcon variant="subtle" color="gray" size="md">
                            <IconDotsVertical size={16} />
                          </ActionIcon>
                        </Menu.Target>
                        <Menu.Dropdown>
                          <Menu.Item
                            leftSection={<IconTrash size={14} />}
                            color="red"
                            onClick={() =>
                              handleDeleteClick({
                                id: file.id,
                                name: file.name,
                              })
                            }
                          >
                            {t("myData").deleteFile}
                          </Menu.Item>
                        </Menu.Dropdown>
                      </Menu>
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        </Stack>
      )}

      {/* Delete Confirmation Modal */}
      <Modal
        opened={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title={t("myData").deleteFileConfirm}
        centered
        size="sm"
      >
        <Text mb="md">
          {t("myData").deleteFileMessage.replace(
            "{name}",
            fileToDelete?.name || ""
          )}
        </Text>
        <Flex justify="flex-end" gap="sm" pt="lg">
          <Button
            variant="outline"
            c="gray.9"
            fullWidth
            onClick={() => setDeleteModalOpen(false)}
            disabled={deleting}
          >
            {t("myData").cancel}
          </Button>
          <Button
            fullWidth
            color="red"
            onClick={handleDeleteConfirm}
            loading={deleting}
          >
            {t("myData").delete}
          </Button>
        </Flex>
      </Modal>
    </Flex>
  );
}
